import {
  Box,
  Button,
  createStyles,
  Dialog,
  Divider,
  makeStyles,
  TextField,
  Theme,
  withStyles
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import {useCallback, useState} from "react";
import {manageParticipantList} from "../../../../store/actions/participant/list";
import {useDispatch, useSelector} from "react-redux";
import UtilService from "../../../../helper/service";
import ParticipantsModal from "./detail/ParticipantsModal";
import {useHistory} from 'react-router-dom';

const useStyles = makeStyles(() => ({
  root: {},
  paper: {
    borderRadius: '24px!important'
  },
  createBtn: {
    width: 91,
    height: 37,
    borderRadius: 6,
    background: '#0179EE',
    color: '#FFFFFF',
    textTransform: 'none',
    fontSize: 14,
    fontFamily: 'roboto-regular',
    '&:hover': {
      background: '#0f9fef'
    },
  },
  cancelBtn: {
    width: 91,
    height: 37,
    borderRadius: 6,
    background: '#F0F0F0',
    color: '#697683',
    textTransform: 'none',
    fontSize: 14,
    fontFamily: 'roboto-regular',
    '&:hover': {
      background: '#e0e0e0',
    },
  },
  textField: {
    "& .MuiOutlinedInput-inputMarginDense": {
      margin: "0!important",
      border: '1px solid #697683',
      padding: 4,
      borderRadius: 3,
      paddingLeft: 6,
      fontSize: 14,
      color: '#717E8B',
    }
  },
  label: {
    "&$focusedLabel": {
      fontSize: 12,
      fontFamily: 'roboto-bold',
      color: "#606B76"
    },
    "&$erroredLabel": {
      color: "#707070"
    }
  },
  focusedLabel: {},
  erroredLabel: {},
  underline: {
    width: '100%',
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    "&$error:after": {
      borderBottomColor: "#017AEF"
    },
    "&:after": {
      borderBottom: `3px solid #017AEF`
    }
  },
  error: {},
  description: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683'
  }
}));

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
      paddingTop: 26
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      background: 'transparent!important'
    },
    header: {
      color: '#212D40',
      fontFamily: 'Spartan-bold',
      fontSize: 24,
      paddingLeft: 17,
      paddingBottom: 7,
      display: 'flex',
      alignItems: 'center'
    }
  });

const DialogTitle = withStyles(styles)((props: any) => {
  const {children, classes, onClose, ...other} = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <div className={classes.header}>
        {children}
        {onClose ? (
          <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
            <img src={'/assets/icons/creatordashboard/project-modal-close.svg'} alt={'img'}/>
          </IconButton>
        ) : null}
      </div>
    </MuiDialogTitle>
  );
});

interface IProps {
  open: boolean,
  onClose: () => void,

}

const CreateList = (props: IProps) => {
  const classes = useStyles();
  const { open, onClose } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const apolloClient = useSelector((state: any) => state.apolloClient);

  const [listName, setListName] = useState('');
  const [participants, setParticipants] = useState([]);
  const [participantsModal, setParticipantsModal] = useState(false);
  const [errorEmailIndex, setErrorEmailIndex] = useState(-1);

  const onChangeListName = useCallback((event: any) => {
    const value = event.target.value;
    if (value === ' ' && listName.length === 0) {
      event.preventDefault();
    } else {
      setListName(value);
    }
  }, [listName]);

  const init = () => {
    setListName('');
    setParticipants([]);
  };

  const onCloseDialog = () => {
    init();
    onClose();
  };

  const onCreateList = useCallback(() => {
    const request = {
      group_name: listName
    };
    dispatch(manageParticipantList(apolloClient.client, request, true, (res: any) => {
      if (res) {
        goListDetailPage(res);
      }
    }));
    onCloseDialog();
  }, [listName]);

  const goListDetailPage = useCallback((list: any) => {
    const url = `/participant/list/${list.group_id}/detail`;
    history.push(url, {listName: list.group_name, listEditable: list.editable});
  }, [history]);

  const handleParticipantsModal = () => {
    const errorEmailIndex = participants.findIndex((item: any) => !UtilService.emailValidate(item.email));
    if (errorEmailIndex < 0) {
      setParticipantsModal(!participantsModal);
      setErrorEmailIndex(-1);
    } else {
      setErrorEmailIndex(errorEmailIndex)
    }
  };

  const onUpdateParticipants = (newParticipants: string[]) => {
    const _newParticipants: any = [];
    newParticipants.forEach((row: any) => {
      _newParticipants.push({ email: row })
    });
    setParticipants(_newParticipants)
  };

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth={true}
      maxWidth={'sm'}
      open={open}
      classes={{
        root: classes.root,
        paper: classes.paper
      }}
    >
      <div>
        <DialogTitle onClose={onCloseDialog}>
          Create a New Participant List
        </DialogTitle>
        <Divider/>

        <Box m={4} pl={1} pr={1}>
          <p className={classes.description}>
            Give your list a name.
          </p>
          <TextField
            InputLabelProps={{
              classes: {
                root: classes.label,
                focused: classes.focusedLabel,
                error: classes.erroredLabel
              },
            }}
            InputProps={{
              classes: {
                root: classes.underline,
                error: classes.error
              }
            }}
            value={listName}
            fullWidth
            margin="normal"
            id="list_name"
            label="List Name"
            name="list_name"
            onChange={onChangeListName}
          />
        </Box>
        <Box mt={6} mb={3} mr={4} display='flex' justifyContent='flex-end'>
          <Button onClick={onCloseDialog} className={classes.cancelBtn}>
            Cancel
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button
            onClick={onCreateList}
            className={classes.createBtn}
            disabled={listName.length === 0}
            style={listName.length === 0 ? {backgroundColor: "#F0F0F0"} : {}}
          >
            Create
          </Button>
        </Box>
      </div>

      <ParticipantsModal
        open={participantsModal}
        participants={participants.map((p: any) => p.email)}
        errorEmailIndex={errorEmailIndex}
        onSaveParticipants={onUpdateParticipants}
        onClose={handleParticipantsModal}
      />
    </Dialog>
  );
};

export default CreateList;